import React from 'react';
import { ExternalLink } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button/button';
import { getServiceConfig } from 'config/serviceConfig';

interface GoogleMeetConnectionModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  /**
   * Redirect URL after successful Google Meet connection
   * @default '/dashboard/events/list'
   */
  redirectTo?: string;
  /**
   * Whether this is a reconnection flow (user previously connected)
   * @default false
   */
  isReconnect?: boolean;
}

const GoogleMeetConnectionModal: React.FC<GoogleMeetConnectionModalProps> = ({
  redirectTo = '/dashboard/events/list',
  isReconnect = false,
  ...modalProps
}) => {
  const handleConnect = (): void => {
    const redirectParam = new URLSearchParams();
    redirectParam.set('redirect', redirectTo);
    const authUrl = getServiceConfig(
      'GOOGLE',
      'GOOGLE_MEET_AUTH_URL',
      `?${redirectParam.toString()}`
    );
    window.location.href = authUrl;
  };

  const handleClose = (): void => {
    modalProps.onOpenChange?.(false);
  };

  return (
    <Modal {...modalProps}>
      <ModalContent size="sm" className="tw-max-w-[483px]">
        <ModalHeader>
          <ModalTitle>{isReconnect ? 'Sign in to Google Meet' : 'Connect Google Meet'}</ModalTitle>
        </ModalHeader>

        <div className="tw-text-sm tw-leading-5">
          {isReconnect ? (
            <p>To continue, please sign in to your Google Meet account.</p>
          ) : (
            <p>
              To generate a Google Meet link, connect your work account for Google.
              <br />
              <br />
              You&apos;ll also get access to additional reporting, such as guest attendance and
              external guests.
            </p>
          )}
        </div>

        <ModalFooter className="tw-flex-col-reverse tw-gap-3 xsm:tw-flex-row">
          <Button
            variant="secondary"
            size="sm"
            className="tw-w-full xsm:tw-w-auto"
            onClick={handleClose}
          >
            Maybe later
          </Button>
          <Button size="sm" className="tw-w-full xsm:tw-w-auto" onClick={handleConnect}>
            <ExternalLink className="tw-mr-2 tw-h-4 tw-w-4" />
            {isReconnect ? 'Sign in' : 'Connect'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { GoogleMeetConnectionModal };
